import React, { useState, useCallback, useEffect } from "react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HActionButton from "../../../hcomponents/HActionButton";
import { Input } from "../../../shadcn/ui/input";
import { Textarea } from "../../../shadcn/ui/textarea";
import { useHistory } from "react-router-dom";
import HAvatar from "../../../hcomponents/HAvatar";
import { Badge } from "../../../shadcn/ui/badge";
import HTabsNavigation from "../../../hcomponents/HTabsNavigation";
import apiService from "../../../services/apiService";
import spaceManagementService from "../../../services/host-apiServices/spaceManagementService";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import moment from "moment";

const CreatePost = () => {
  const [activeTab, setActiveTab] = useState("edit");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const history = useHistory();

  const handleFileUpload = async files => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Convert FileList to Array
      const fileArray = Array.from(files);

      // Upload files using spaceManagementService
      const response = await spaceManagementService.uploadMedia(fileArray);

      if (response.status === 200 || response.status === 201) {
        const uploadedUrls = response.data;
        setImageUrls(prev => [...prev, ...uploadedUrls]);
        setUploadProgress(100);
        showApiMessage(response, "upload images");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      logger.error("Error uploading files:", error);
      showApiMessage(error, "upload images");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = indexToRemove => {
    setImageUrls(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    if (!title.trim()) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Title is required" },
          },
        },
        "validation error"
      );
      return false;
    }

    if (!description.trim()) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Description is required" },
          },
        },
        "validation error"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Create datetime for scheduling
      let datetimeToSend = null;
      if (scheduleDate && scheduleTime) {
        const [hours, minutes] = scheduleTime.split(":");
        const scheduledDate = new Date(scheduleDate);
        scheduledDate.setHours(hours, minutes, 0);
        datetimeToSend = moment(scheduledDate).utc().format("YYYY-MM-DDTHH:mm:ss");
      }

      const payload = {
        title,
        message: description,
        roles: [],
        userIds: [],
        imageUrls,
        datetimeToSend,
        priority: "LOW",
        hasCTA: false,
        actionType: "announcement",
        topic: "b2b-announcements",
        data: {},
      };

      const response = await apiService.createAnnouncement(payload);

      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create announcement");
        history.push("/host/feed");
      }
    } catch (error) {
      logger.error("Error creating announcement:", error);
      showApiMessage(error, "create announcement");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HPageContainer>
      <div className="flex flex-col h-full">
        <div className="py-8 mx-auto w-full max-w-3xl">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Create Post</h1>
            <p className="text-sm text-gray-500">
              Share announcements, events, or menu updates with your community members.
            </p>
          </div>

          <div className="p-6 mb-6 bg-white rounded-2xl shadow-sm">
            {/* Tabs */}
            <HTabsNavigation
              tabs={[
                { label: "Edit Post", value: "edit" },
                { label: "Preview", value: "preview" },
              ]}
              value={activeTab}
              onTabChange={setActiveTab}
              showMobileDropdown={false}
              className="mx-0 mt-0 mb-6"
              activeTabStyle="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow data-[state=active]:rounded-[24px]"
              inactiveTabStyle="data-[state=inactive]:text-gray-700"
              containerStyle="border border-[#E2E4EA] rounded-2xl bg-[#F6F8FC] p-2"
            />

            {/* Form */}
            {activeTab === "edit" && (
              <form className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    maxLength={100}
                    placeholder="Enter post title"
                  />
                  <div className="text-xs text-right text-gray-400">{title.length}/100</div>
                </div>
                {/* Description */}
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    maxLength={500}
                    placeholder="Enter post description"
                  />
                  <div className="text-xs text-right text-gray-400">{description.length}/500</div>
                </div>
                {/* Image Upload */}
                <div>
                  <label className="block mb-1 text-sm font-medium">Images (Optional)</label>
                  <div className="space-y-3">
                    {/* File Upload Input */}
                    <div className="flex items-center justify-center w-full">
                      <label
                        htmlFor="imageUpload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 mb-4 text-gray-500"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 16"
                          >
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                          </svg>
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> images
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 10MB each)</p>
                        </div>
                        <input
                          id="imageUpload"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={e => handleFileUpload(e.target.files)}
                          className="hidden"
                          disabled={isUploading}
                        />
                      </label>
                    </div>

                    {/* Upload Progress */}
                    {isUploading && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    )}

                    {/* Uploaded Images Preview */}
                    {imageUrls.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {imageUrls.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Uploaded ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Upload images to include in the notification</p>
                </div>
                {/* Schedule Post */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block mb-1 text-sm font-medium">Schedule Post (Optional)</label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={e => setScheduleDate(e.target.value)}
                        className="w-1/2"
                        placeholder="Select date"
                      />
                      <Input
                        type="time"
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                        className="w-1/2"
                        placeholder="--:-- --"
                      />
                    </div>
                  </div>
                </div>
                {/* Buttons */}
                <div className="flex gap-2 mt-4">
                  <HActionButton
                    label="Send Announcement"
                    variant="primary"
                    size="medium"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={isSubmitting || isUploading}
                  />
                </div>
              </form>
            )}
            {activeTab === "preview" && (
              <div className="p-6 bg-white rounded-2xl shadow-sm">
                {/* User info and badges row */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-3 items-center">
                    <HAvatar src={null} alt="Easin Arafat" className="w-10 h-10" />
                    <div>
                      <div className="font-semibold leading-tight text-gray-900">Easin Arafat</div>
                      <div className="text-xs text-gray-500">Super Host Admin • Just now</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="flex gap-1 items-center px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100">
                      <span className="inline-block mr-1 w-2 h-2 bg-gray-400 rounded-full"></span>
                      event
                    </Badge>
                    <Badge className="flex gap-1 items-center px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100">
                      <span className="inline-block mr-1 w-2 h-2 bg-gray-400 rounded-full"></span>
                      Public
                    </Badge>
                  </div>
                </div>
                {/* Title and description */}
                <div className="mb-2">
                  <div className="mb-1 text-lg font-bold text-gray-900">{title || "horizons offices spa day"}</div>
                  <div className="text-sm text-gray-500">{description || "This is a sample for the preview"}</div>
                </div>
                {/* Image preview */}
                {imageUrls.length > 0 ? (
                  <div className="flex justify-center mt-4">
                    <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
                      {imageUrls.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center mt-4">
                    <div className="flex overflow-hidden relative flex-col justify-center items-center p-4 w-full max-w-lg h-64 bg-gray-100 rounded-xl">
                      <span className="text-gray-400">No images uploaded</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Guidelines */}
          <div className="flex flex-col gap-8 p-6 mt-8 bg-white rounded-2xl shadow-sm md:flex-row">
            <div className="flex-1">
              <h4 className="mb-2 font-semibold">Content Guidelines</h4>
              <ul className="pl-5 space-y-1 text-sm list-disc text-gray-600">
                <li>Keep posts relevant to your community</li>
                <li>Use clear, descriptive titles</li>
                <li>Include relevant images when possible</li>
                <li>Respect community guidelines</li>
              </ul>
            </div>
            <div className="flex-1">
              <h4 className="mb-2 font-semibold">Best Practices</h4>
              <ul className="pl-5 space-y-1 text-sm list-disc text-gray-600">
                <li>Schedule posts for optimal engagement</li>
                <li>Use appropriate visibility settings</li>
                <li>Preview your post before publishing</li>
                <li>Engage with community responses</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </HPageContainer>
  );
};

export default CreatePost;
